import { Link } from "@tanstack/react-router";
import {
  AlertCircle,
  BookOpen,
  Download,
  FileDown,
  Loader2,
  NotebookPen,
  Share2,
  UsersRound,
} from "lucide-react";
import { useEffect, useState } from "react";

import { SectionLabel } from "components/AppShell";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import {
  ResourceFailureScope,
  ResourceOpenControl,
  useResourceDownload,
} from "../event-experience/ResourceOpenLink";
import type { Resource } from "../event-experience/schemas";
import { useVisibleResources } from "../event-experience/use-event-content";
import { deliverFile, isIOS } from "./deliver-file";
import { loadSignalsSummary, summaryFileName } from "./signals-summary";
import {
  hasVisibleHtml,
  useAfterEventSummary,
} from "./use-after-event-summary";

/**
 * The "After the event" section at the top of the Overview (TNG-96).
 *
 * Mounted only once it should show (afterEventVisible, or an admin's
 * preview), so nothing here is read before then. It holds the organiser's
 * summary, the resources marked "after the event", the signals summary PDF
 * and a way to the member's notes.
 *
 * The resources come from the same audience-scoped queries as the Resources
 * tab, filtered to `afterEvent`, so a resource limited to some tables stays
 * limited here too.
 */
export function AfterEventSection({
  eventId,
  eventTitle,
  timeZone,
  uid,
  seeAllResources,
  hasSignals,
  connectionsEnabled = false,
}: {
  eventId: string;
  eventTitle: string;
  timeZone: string;
  uid: string;
  seeAllResources: boolean;
  hasSignals: boolean;
  /** The event's configConnections: adds the link to its connections. */
  connectionsEnabled?: boolean;
}) {
  const summary = useAfterEventSummary(eventId);
  const resources = useVisibleResources(eventId, uid, seeAllResources);
  const afterEventResources = afterEventOnly(
    resources.status === "success" ? resources.data : [],
  );
  const summaryHtml =
    summary.status === "success" && hasVisibleHtml(summary.summary?.html)
      ? (summary.summary?.html ?? "")
      : null;

  return (
    <section className="space-y-3" data-testid="after-event-section">
      <SectionLabel>After the event</SectionLabel>
      {summaryHtml !== null && (
        <Card
          data-testid="after-event-summary"
          className="border-lit-card-edge shadow-lit-card"
        >
          <CardContent
            className="prose prose-sm max-w-none pt-6 dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-a:text-lit"
            // Sanitised by the SAVE_AFTER_EVENT_SUMMARY trigger before it
            // was stored; clients cannot write this document.
            dangerouslySetInnerHTML={{ __html: summaryHtml }}
          />
        </Card>
      )}
      {summary.status === "error" && (
        <p className="flex items-center gap-2 text-sm text-hb-red-text">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Could not load the organiser&apos;s summary.
        </p>
      )}
      {afterEventResources.length > 0 && (
        <ResourceFailureScope testId="after-event-resources-failed">
          <div className="space-y-2" data-testid="after-event-resources">
            {afterEventResources.map((resource) => (
              <AfterEventResourceRow key={resource.id} resource={resource} />
            ))}
          </div>
        </ResourceFailureScope>
      )}
      <Card>
        <CardContent className="flex flex-col gap-2 py-4 sm:flex-row sm:flex-wrap">
          {hasSignals && (
            <SignalsSummaryButton
              eventId={eventId}
              eventTitle={eventTitle}
              timeZone={timeZone}
            />
          )}
          <Button
            asChild
            variant="outline"
            className="h-11"
            data-testid="after-event-notes-link"
          >
            <Link to="/notes">
              <NotebookPen className="h-4 w-4" />
              My notes
            </Link>
          </Button>
          {connectionsEnabled && (
            <Button
              asChild
              variant="outline"
              className="h-11"
              data-testid="after-event-connections-link"
            >
              <Link to="/connections" search={{ event: eventId }}>
                <UsersRound className="h-4 w-4" />
                Your connections from this event
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/** The resources the organiser marked for after the event, in listing order. */
export function afterEventOnly(resources: readonly Resource[]): Resource[] {
  return resources.filter((resource) => resource.afterEvent === true);
}

function AfterEventResourceRow({ resource }: { resource: Resource }) {
  const download = useResourceDownload(resource.storagePath);
  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-3">
        <BookOpen className="h-5 w-5 shrink-0 text-lit" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-foreground">
            {resource.name}
          </p>
          {resource.description && (
            <p className="truncate text-sm text-muted-foreground">
              {resource.description}
            </p>
          )}
          {download.status === "error" && (
            <p className="text-sm text-hb-red-text">
              Could not load that file.
            </p>
          )}
        </div>
        <ResourceOpenControl
          download={download}
          name={resource.name}
          testId={`after-event-open-resource-${resource.id}`}
        />
      </CardContent>
    </Card>
  );
}

type PdfState =
  | { status: "idle" }
  | { status: "working" }
  | { status: "ready" }
  | { status: "done"; message: string }
  | { status: "cancelled" }
  | { status: "error"; message: string };

/**
 * Builds the PDF on the tap and hands it over (deliverFile). Once built, a
 * plain Download link stays beside the button, so the file is still to hand
 * after a closed share sheet or a download the browser blocked. On iOS a
 * "Share again" button stands in for that link.
 */
function SignalsSummaryButton({
  eventId,
  eventTitle,
  timeZone,
}: {
  eventId: string;
  eventTitle: string;
  timeZone: string;
}) {
  const [state, setState] = useState<PdfState>({ status: "idle" });
  const [file, setFile] = useState<{ blob: Blob; url: string } | null>(null);
  const fileName = summaryFileName(eventTitle);
  const title = `${eventTitle}: signals summary`;
  const ios = isIOS();

  useEffect(
    () => () => {
      if (file) URL.revokeObjectURL(file.url);
    },
    [file],
  );

  async function deliver(blob: Blob) {
    const outcome = await deliverFile(blob, fileName, title);
    if (outcome === "needs-tap") setState({ status: "ready" });
    else if (outcome === "cancelled") setState({ status: "cancelled" });
    else
      setState({
        status: "done",
        message: outcome === "shared" ? "Shared." : "Downloaded.",
      });
  }

  async function build() {
    setState({ status: "working" });
    try {
      const rows = await loadSignalsSummary(eventId);
      const { renderSignalsSummaryPdf } = await import("./signals-summary-pdf");
      const blob = await renderSignalsSummaryPdf(rows, {
        eventTitle,
        generatedAt: new Date(),
        timeZone,
      });
      setFile({ blob, url: URL.createObjectURL(blob) });
      await deliver(blob);
    } catch (error) {
      setState({
        status: "error",
        message:
          error instanceof Error
            ? `Could not make the PDF: ${error.message}`
            : "Could not make the PDF.",
      });
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-2 sm:flex-row">
        {state.status === "ready" && file ? (
          <Button
            variant="cta"
            className="h-11"
            onClick={() => void deliver(file.blob)}
            data-testid="after-event-signals-pdf-share"
          >
            <Share2 className="h-4 w-4" />
            Share the PDF
          </Button>
        ) : (
          <Button
            variant="cta"
            className="h-11"
            disabled={state.status === "working"}
            onClick={() => void build()}
            data-testid="after-event-signals-pdf"
          >
            {state.status === "working" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            Signals summary (PDF)
          </Button>
        )}
        {/* On iOS a download link opens the file in a view with no way
            back into a home-screen app (deliver-file.ts), so the file is
            offered to the share sheet again instead. */}
        {file &&
          ios &&
          (state.status === "done" || state.status === "cancelled") && (
            <Button
              variant="outline"
              className="h-11"
              onClick={() => void deliver(file.blob)}
              data-testid="after-event-signals-pdf-share-again"
            >
              <Share2 className="h-4 w-4" />
              Share again
            </Button>
          )}
        {file && !ios && state.status !== "working" && (
          <Button asChild variant="outline" className="h-11">
            <a
              href={file.url}
              download={fileName}
              data-testid="after-event-signals-pdf-download"
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          </Button>
        )}
      </div>
      {state.status === "done" && (
        <p
          role="status"
          className="text-xs text-muted-foreground"
          data-testid="after-event-signals-pdf-status"
        >
          {state.message}
        </p>
      )}
      {state.status === "error" && (
        <p role="alert" className="text-xs text-hb-red-text">
          {state.message}
        </p>
      )}
    </div>
  );
}
